import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Popup to show selected file overview (3–5 rows preview) with Apply and Cancel.
 * Apply/Cancel both close and reset; API integration can be added to onApply later.
 */
const UploadPreviewPopUp = ({
  open = false,
  onClose,
  title = 'Upload - File overview',
  fileName = '',
  previewHeaders = [],
  previewRows = [],
  onApply,
  onCancel,
  loading = false,
}) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (onCancel) onCancel();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose, onCancel]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleCancel();
  };

  const handleApply = async () => {
    if (!onApply) {
      onClose();
      return;
    }
    const result = await Promise.resolve(onApply());
    if (result !== false) onClose();
  };

  const handleCancel = () => {
    if (loading) return; // Prevent cancel during loading
    if (onCancel) onCancel();
    onClose();
  };

  if (!open) return null;

  const hasPreview = previewHeaders.length > 0 && previewRows.length > 0;

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-preview-title"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-2xl max-h-[90vh] mx-4 bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-2 border-b border-gray-100">
          <h2 id="upload-preview-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-auto flex-1 min-h-0">
          {fileName && (
            <p className="text-sm text-gray-600 mb-3">
              <span className="font-medium">File:</span> {fileName}
            </p>
          )}
          {hasPreview ? (
            <div className="border border-gray-200 rounded-lg overflow-x-auto overflow-y-hidden">
              <table className="w-max min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {previewHeaders.map((h, i) => (
                      <th
                        key={i}
                        className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap"
                      >
                        {h || `Column ${i + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rIdx) => (
                    <tr
                      key={rIdx}
                      className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50"
                    >
                      {previewHeaders.map((key, cIdx) => (
                        <td key={cIdx} className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {row[key] ?? '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No rows to preview.</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={loading}
            className="h-10 rounded-full border border-emerald-500 bg-emerald-500 px-5 text-sm font-medium text-white hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {loading ? 'Uploading...' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default UploadPreviewPopUp;
